import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

interface Video {
  id: number
  title: string
  description: string | null
  file_path: string
  file_url: string
  display_order: number
  visible_on_home: boolean
  visible_on_affiliate_landing: boolean
  visible_on_shareholder_dashboard: boolean
  visible_on_affiliate_dashboard: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

interface VideoFormData {
  title: string
  description: string
  visible_on_home: boolean
  visible_on_affiliate_landing: boolean
  visible_on_shareholder_dashboard: boolean
  visible_on_affiliate_dashboard: boolean
  is_active: boolean
}

export const AdminHowToStartManager: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // Upload state
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  
  // Form state
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [editingVideo, setEditingVideo] = useState<Video | null>(null)
  const [formData, setFormData] = useState<VideoFormData>({
    title: '',
    description: '',
    visible_on_home: false,
    visible_on_affiliate_landing: false,
    visible_on_shareholder_dashboard: false,
    visible_on_affiliate_dashboard: false,
    is_active: true
  })

  useEffect(() => {
    fetchVideos()
  }, [])

  const fetchVideos = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Not authenticated')
        return
      }

      const response = await fetch('/api/admin/how-to-start-videos', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch videos')
      }

      const data = await response.json()
      setVideos(data.videos || [])
    } catch (err) {
      console.error('Error fetching videos:', err)
      setError('Failed to load videos')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload MP4, WebM, MOV, AVI, or MKV files.')
      return
    }

    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024 // 500MB in bytes
    if (file.size > maxSize) {
      setError('File size exceeds 500MB limit')
      return
    }

    setSelectedFile(file)
    setError(null)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a video file')
      return
    }

    if (!formData.title.trim()) {
      setError('Please enter a video title')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Generate unique file name
      const timestamp = Date.now()
      const fileExt = selectedFile.name.split('.').pop()
      const fileName = `${timestamp}_${formData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${fileExt}`
      const filePath = fileName

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('HowToStart')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`)
      }

      setUploadProgress(50)

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('HowToStart')
        .getPublicUrl(filePath)

      setUploadProgress(75)

      // Create database record
      const response = await fetch('/api/admin/how-to-start-videos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description || null,
          file_path: filePath,
          file_url: publicUrl,
          display_order: videos.length, // Add to end
          visible_on_home: formData.visible_on_home,
          visible_on_affiliate_landing: formData.visible_on_affiliate_landing,
          visible_on_shareholder_dashboard: formData.visible_on_shareholder_dashboard,
          visible_on_affiliate_dashboard: formData.visible_on_affiliate_dashboard,
          is_active: formData.is_active
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create video record')
      }

      setUploadProgress(100)
      setSuccessMessage('Video uploaded successfully!')
      
      // Reset form
      setSelectedFile(null)
      setFormData({
        title: '',
        description: '',
        visible_on_home: false,
        visible_on_affiliate_landing: false,
        visible_on_shareholder_dashboard: false,
        visible_on_affiliate_dashboard: false,
        is_active: true
      })
      setShowUploadForm(false)

      // Refresh videos list
      await fetchVideos()

      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      console.error('Upload error:', err)
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleEdit = (video: Video) => {
    setEditingVideo(video)
    setFormData({
      title: video.title,
      description: video.description || '',
      visible_on_home: video.visible_on_home,
      visible_on_affiliate_landing: video.visible_on_affiliate_landing,
      visible_on_shareholder_dashboard: video.visible_on_shareholder_dashboard,
      visible_on_affiliate_dashboard: video.visible_on_affiliate_dashboard,
      is_active: video.is_active
    })
  }

  const handleUpdate = async () => {
    if (!editingVideo) return

    try {
      setUploading(true)
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`/api/admin/how-to-start-videos?id=${editingVideo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description || null,
          visible_on_home: formData.visible_on_home,
          visible_on_affiliate_landing: formData.visible_on_affiliate_landing,
          visible_on_shareholder_dashboard: formData.visible_on_shareholder_dashboard,
          visible_on_affiliate_dashboard: formData.visible_on_affiliate_dashboard,
          is_active: formData.is_active
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update video')
      }

      setSuccessMessage('Video updated successfully!')
      setEditingVideo(null)
      setFormData({
        title: '',
        description: '',
        visible_on_home: false,
        visible_on_affiliate_landing: false,
        visible_on_shareholder_dashboard: false,
        visible_on_affiliate_dashboard: false,
        is_active: true
      })

      await fetchVideos()
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      console.error('Update error:', err)
      setError(err instanceof Error ? err.message : 'Update failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (video: Video) => {
    if (!confirm(`Are you sure you want to delete "${video.title}"? This action cannot be undone.`)) {
      return
    }

    try {
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`/api/admin/how-to-start-videos?id=${video.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to delete video')
      }

      setSuccessMessage('Video deleted successfully!')
      await fetchVideos()
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      console.error('Delete error:', err)
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  const moveVideo = async (video: Video, direction: 'up' | 'down') => {
    const currentIndex = videos.findIndex(v => v.id === video.id)
    if (currentIndex === -1) return
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= videos.length) return

    try {
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Swap display orders
      const videoOrders = [
        { id: videos[currentIndex].id, display_order: videos[newIndex].display_order },
        { id: videos[newIndex].id, display_order: videos[currentIndex].display_order }
      ]

      const response = await fetch('/api/admin/how-to-start-videos-reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ videoOrders })
      })

      if (!response.ok) {
        throw new Error('Failed to reorder videos')
      }

      await fetchVideos()
    } catch (err) {
      console.error('Reorder error:', err)
      setError(err instanceof Error ? err.message : 'Reorder failed')
    }
  }

  return (
    <div className="admin-how-to-start-manager">
      <div className="manager-header">
        <h2>How To Start Videos Management</h2>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="btn-primary"
          disabled={uploading}
        >
          {showUploadForm ? 'Cancel Upload' : '+ Upload New Video'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Upload/Edit Form */}
      {(showUploadForm || editingVideo) && (
        <div className="upload-form">
          <h3>{editingVideo ? 'Edit Video' : 'Upload New Video'}</h3>

          {!editingVideo && (
            <div className="form-group">
              <label className="form-label">Video File *</label>
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
                onChange={handleFileSelect}
                disabled={uploading}
                className="file-input"
              />
              {selectedFile && (
                <p className="file-info">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              <p className="form-help">Max file size: 500MB. Supported formats: MP4, WebM, MOV, AVI, MKV</p>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              disabled={uploading}
              className="form-input"
              placeholder="Enter video title"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={uploading}
              className="form-textarea"
              placeholder="Enter video description (optional)"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Visibility Settings</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visible_on_home}
                  onChange={(e) => setFormData({ ...formData, visible_on_home: e.target.checked })}
                  disabled={uploading}
                />
                <span>Show on Home Page</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visible_on_affiliate_landing}
                  onChange={(e) => setFormData({ ...formData, visible_on_affiliate_landing: e.target.checked })}
                  disabled={uploading}
                />
                <span>Show on Affiliate Landing Page</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visible_on_shareholder_dashboard}
                  onChange={(e) => setFormData({ ...formData, visible_on_shareholder_dashboard: e.target.checked })}
                  disabled={uploading}
                />
                <span>Show on Shareholder Dashboard</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.visible_on_affiliate_dashboard}
                  onChange={(e) => setFormData({ ...formData, visible_on_affiliate_dashboard: e.target.checked })}
                  disabled={uploading}
                />
                <span>Show on Affiliate Dashboard</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                disabled={uploading}
              />
              <span>Active (Published)</span>
            </label>
          </div>

          {uploading && uploadProgress > 0 && (
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              <span className="progress-text">{uploadProgress}%</span>
            </div>
          )}

          <div className="form-actions">
            <button
              onClick={editingVideo ? handleUpdate : handleUpload}
              disabled={uploading || (!editingVideo && !selectedFile)}
              className="btn-primary"
            >
              {uploading ? 'Processing...' : editingVideo ? 'Update Video' : 'Upload Video'}
            </button>
            <button
              onClick={() => {
                setShowUploadForm(false)
                setEditingVideo(null)
                setSelectedFile(null)
                setFormData({
                  title: '',
                  description: '',
                  visible_on_home: false,
                  visible_on_affiliate_landing: false,
                  visible_on_shareholder_dashboard: false,
                  visible_on_affiliate_dashboard: false,
                  is_active: true
                })
              }}
              disabled={uploading}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Videos List */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading videos...</p>
        </div>
      ) : videos.length === 0 ? (
        <div className="empty-state">
          <p>No videos uploaded yet. Click "Upload New Video" to get started.</p>
        </div>
      ) : (
        <div className="videos-list">
          <h3>Uploaded Videos ({videos.length})</h3>
          {videos.map((video, index) => (
            <div key={video.id} className="video-item">
              <div className="video-info">
                <div className="video-order">#{index + 1}</div>
                <div className="video-details">
                  <h4>{video.title}</h4>
                  {video.description && <p className="video-desc">{video.description}</p>}
                  <div className="video-meta">
                    <span className={`status-badge ${video.is_active ? 'active' : 'inactive'}`}>
                      {video.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {video.visible_on_home && <span className="visibility-badge">Home</span>}
                    {video.visible_on_affiliate_landing && <span className="visibility-badge">Affiliate Landing</span>}
                    {video.visible_on_shareholder_dashboard && <span className="visibility-badge">Shareholder</span>}
                    {video.visible_on_affiliate_dashboard && <span className="visibility-badge">Affiliate</span>}
                  </div>
                </div>
              </div>
              <div className="video-actions">
                <button
                  onClick={() => moveVideo(video, 'up')}
                  disabled={index === 0}
                  className="btn-icon"
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  onClick={() => moveVideo(video, 'down')}
                  disabled={index === videos.length - 1}
                  className="btn-icon"
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  onClick={() => handleEdit(video)}
                  className="btn-edit"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(video)}
                  className="btn-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

